import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { addVendor, fetchVendors } from '../../features/vendors/vendorSlice';
import { useFormValidation } from '../../hooks/useFormValidation';
import Card from '../ui/Card';
import Button from '../ui/Button';
import VendorFormFields from './VendorFormFields';

interface Props {
  onSuccess: () => void;
}

const AddVendorForm: React.FC<Props> = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { values, errors, handleChange, handleSubmit, isValid } = useFormValidation(
    {
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
    },
    {
      name: (value) => (!value ? t('vendors.validation.nameRequired') : ''),
      contact_person: (value) => (!value ? t('vendors.validation.contactPersonRequired') : ''),
      phone: (value) => {
        if (!value) return '';
        if (!/^[0-9+\- ]{10,15}$/.test(value)) {
          return t('vendors.validation.phoneInvalid');
        }
        return '';
      },
      email: (value) => {
        if (value && !/\S+@\S+\.\S+/.test(value)) {
          return t('vendors.validation.emailInvalid');
        }
        return '';
      },
    },
    async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        await dispatch(addVendor(values));
        await dispatch(fetchVendors()); // Refresh vendors list
        onSuccess();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.unknownError');
        setSubmitError(errorMessage);
        console.error('Error adding vendor:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  );

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-4">
        {submitError && (
          <div className="text-red-600 text-sm">{submitError}</div>
        )}
        
        <VendorFormFields
          values={values}
          errors={errors}
          onChange={handleChange}
          disabled={isSubmitting}
        />

        <div className="flex justify-end space-x-2">
          <Button 
            type="button" 
            variant="secondary" 
            onClick={onSuccess}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit" 
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? t('common.loading') : t('vendors.addVendor')}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default AddVendorForm;